import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from '@mui/material';
import { holdingsApi, supportingDataApi } from '@/services/api';
import { formatDate, formatNumber, formatPercentage, formatCurrencyInCrores } from '@/utils/formatters';
import type { SchemeWiseHoldingsSummary, PivotedSchemeRow, AssetClass } from '@/types/client';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useHoldingsDateRestriction } from '@/hooks/useHoldingsDateRestriction';
import { useAnchoredPopover } from '@/hooks/useAnchoredPopover';
import { isAifScheme } from '@/hooks/useSchemeOptions';
import { LastWorkingDateFilter } from '@/components/utils/filters';
import SchemeFilterPanel from '@/components/utils/SchemeFilterPanel';
import type { SchemeOption } from '@/components/utils/SchemeFilterPanel';
import { pageStyles } from '@/components/utils/PageLayout';
import { PageFrame, PageHeader, Surface, Note, Placeholder, Icon, Button, StatGrid, loadingStats } from '@/components/ui';
import type { StatItem } from '@/components/ui';
import s from './SchemeWiseHoldings.module.css';


interface SortConfig {
  field: string; // 'symbol' or a scheme id string
  dir: 'asc' | 'desc';
}


const ASSET_CLASS_MOD: Record<string, string> = {
  Equity: 'badge-equity',
  Futures: 'badge-futures',
};

const DMF_INVESTMENT_STRATEGY_ID = '4';


const SchemeWiseHoldings: React.FC = () => {
  const [pivotedRows, setPivotedRows] = useState<PivotedSchemeRow[]>([]);
  const [schemeTotals, setSchemeTotals] = useState<Record<string, { market_value: number; percentage_of_aum: number }>>({});
  const [loading, setLoading] = useState(false);
  const [holdingsError, setHoldingsError] = useState<string | null>(null);
  const [schemesError, setSchemesError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [regionSchemes, setRegionSchemes] = useState<SchemeOption[]>([]);
  const [hiddenSchemeIds, setHiddenSchemeIds] = useState<Set<string>>(new Set());
  const [hiddenAssetClassIds, setHiddenAssetClassIds] = useState<Set<number>>(new Set());
  const [assetClasses, setAssetClasses] = useState<AssetClass[]>([]);
  const [assetClassesLoaded, setAssetClassesLoaded] = useState(false);
  const [showAssetClassDropdown, setShowAssetClassDropdown] = useState(false);
  const [excludeInherited, setExcludeInherited] = useState<boolean>(true);
  const [summary, setSummary] = useState<SchemeWiseHoldingsSummary | null>(null);
  const [isAccessRestricted, setIsAccessRestricted] = useState(false);
  const { accessCutoffDate, restrictionLoaded, isDateRestricted, clampDate } = useHoldingsDateRestriction();

  // When restriction loads after the date is already set, snap back to accessCutoffDate
  useEffect(() => {
    if (isDateRestricted(selectedDate)) {
      setSelectedDate(clampDate(selectedDate));
    }
  }, [accessCutoffDate, selectedDate]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'symbol', dir: 'asc' });
  const [row1Height, setRow1Height] = useState(0);
  const headerRow1Ref = useCallback((el: HTMLTableRowElement | null) => {
    if (el) setRow1Height(el.offsetHeight);
  }, []);

  const closeAssetClassDropdown = useCallback(() => setShowAssetClassDropdown(false), []);
  const { anchorRef: assetClassBtnRef, panelRef: assetClassPanelRef, pos: assetClassPos } =
    useAnchoredPopover(showAssetClassDropdown, closeAssetClassDropdown, 200);

  const error = schemesError || holdingsError;

  // Load all asset classes once; default to Equity only
  useEffect(() => {
    let cancelled = false;
    supportingDataApi.getAssetClasses()
      .then(data => {
        if (!cancelled) {
          setAssetClasses(data);
          setHiddenAssetClassIds(new Set(data.filter(ac => ac.name !== 'Equity').map(ac => ac.id)));
        }
      })
      .catch((err: any) => { if (!cancelled) setHoldingsError(err.response?.data?.message || 'Failed to load asset classes'); })
      .finally(() => { if (!cancelled) setAssetClassesLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Auto-reveal Mutual Fund asset class when any active scheme has DMF investment strategy,
  // and hide it again when no DMF scheme is active.
  useEffect(() => {
    const mutualFundClass = assetClasses.find(ac => ac.name === 'Mutual Fund');
    if (!mutualFundClass) return;
    const hasDmfScheme = regionSchemes
      .filter(sc => !hiddenSchemeIds.has(sc.value))
      .some(sc => sc.investmentStrategy === DMF_INVESTMENT_STRATEGY_ID);
    setHiddenAssetClassIds(prev => {
      const isHidden = prev.has(mutualFundClass.id);
      if (hasDmfScheme && isHidden) {
        const next = new Set(prev);
        next.delete(mutualFundClass.id);
        return next;
      }
      if (!hasDmfScheme && !isHidden) {
        const next = new Set(prev);
        next.add(mutualFundClass.id);
        return next;
      }
      return prev;
    });
  }, [regionSchemes, hiddenSchemeIds, assetClasses]);

  // Active scheme IDs: all region schemes minus those hidden by the user
  const activeSchemeIds = useMemo(
    () => regionSchemes.filter(sc => !hiddenSchemeIds.has(sc.value)).map(sc => sc.value).join(','),
    [regionSchemes, hiddenSchemeIds],
  );

  // Debounced so rapid checkbox toggling doesn't spam the API — but the first
  // list, when the schemes arrive, goes straight through.
  const debouncedSchemeIds = useDebouncedValue(activeSchemeIds, 400);

  // Active asset class IDs:
  //   ''         → no filter (all visible), backend returns everything
  //   '1,2'      → filter to these IDs
  //   '__none__' → all hidden, skip fetch and show empty results
  const activeAssetClassIds = useMemo(() => {
    if (hiddenAssetClassIds.size === 0) return '';
    const visible = assetClasses.filter(ac => !hiddenAssetClassIds.has(ac.id));
    if (visible.length === 0) return '__none__';
    return visible.map(ac => ac.id).join(',');
  }, [assetClasses, hiddenAssetClassIds]);

  // '' is a real value here (no filter), so "ready" means the asset classes have loaded.
  const debouncedAssetClassIds = useDebouncedValue(activeAssetClassIds, 400, () => assetClassesLoaded);

  // The first fetch waits for every filter it depends on (all requested in
  // parallel on mount). Until then the page shows the loader, not "No data".
  const filtersReady = !!selectedDate && assetClassesLoaded && restrictionLoaded && regionSchemes.length > 0;
  // A filter change (region switch, scheme or asset-class toggle) waits out the
  // debounce before its fetch starts; the rows on screen no longer match it, so
  // keep the loader up rather than flashing "No holdings" in between.
  const filtersPending = activeSchemeIds !== debouncedSchemeIds || activeAssetClassIds !== debouncedAssetClassIds;
  const showLoader = loading || (!error && (!filtersReady || filtersPending));

  // Fetch holdings for the selected date + visible schemes + visible asset classes
  useEffect(() => {
    if (!filtersReady || isDateRestricted(selectedDate)) return;
    if (!debouncedSchemeIds) {
      if (!debouncedSchemeIds) {
        setPivotedRows([]);
        setSchemeTotals({});
        setSummary(null);
      }
      return;
    }
    if (debouncedAssetClassIds === '__none__') {
      setPivotedRows([]);
      setSchemeTotals({});
      setSummary(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setHoldingsError(null);
    setIsAccessRestricted(false);
    setPivotedRows([]);
    setSchemeTotals({});
    setSummary(null);
    const params: { date: string; scheme_ids: string; asset_class_ids?: string; exclude_inherited?: string } = {
      date: selectedDate,
      scheme_ids: debouncedSchemeIds,
      exclude_inherited: excludeInherited ? 'true' : 'false',
    };
    if (debouncedAssetClassIds) params.asset_class_ids = debouncedAssetClassIds;
    holdingsApi
      .getHoldingsByScheme(params)
      .then(response => {
        if (cancelled) return;
        if (response.access_restricted) {
          setIsAccessRestricted(true);
          return;
        }
        setPivotedRows(response.results);
        setSchemeTotals(response.scheme_totals);
        setSummary(response.summary);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setHoldingsError(err.response?.data?.message || 'Failed to load holdings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filtersReady, selectedDate, debouncedSchemeIds, debouncedAssetClassIds, excludeInherited, accessCutoffDate]);

  const toggleAssetClass = (id: number) => {
    setHiddenAssetClassIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Visible schemes (not hidden, and have holdings data)
  const holdingsByScheme = useMemo(() => {
    const map: Record<string, boolean> = {};
    pivotedRows.forEach(row => {
      Object.keys(row.schemes).forEach(id => { map[id] = true; });
    });
    return map;
  }, [pivotedRows]);

  // Visible schemes sorted by AUM descending (highest AUM on the left)
  const visibleSchemes = useMemo(() => {
    return regionSchemes
      .filter(sc => !hiddenSchemeIds.has(sc.value) && holdingsByScheme[sc.value])
      .sort((a, b) => (summary?.scheme_aum[b.value] ?? 0) - (summary?.scheme_aum[a.value] ?? 0));
  }, [regionSchemes, hiddenSchemeIds, holdingsByScheme, summary]);

  // Summary computed from visible schemes only
  const visibleSummary = useMemo(() => {
    let totalAum = 0;
    visibleSchemes.forEach(sc => {
      totalAum += summary?.scheme_aum[sc.value] ?? 0;
    });
    return { totalAum, schemeCount: visibleSchemes.length };
  }, [visibleSchemes, summary]);

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field ? (prev.dir === 'asc' ? 'desc' : 'asc') : field === 'symbol' ? 'asc' : 'desc',
    }));
  };

  const sortedRows = useMemo(() => {
    return [...pivotedRows]
      .filter(row => Object.keys(row.schemes).some(id => !hiddenSchemeIds.has(id)))
      .sort((a, b) => {
        const mul = sortConfig.dir === 'asc' ? 1 : -1;
        if (sortConfig.field === 'symbol') return mul * a.symbol.localeCompare(b.symbol);
        const aPct = a.schemes[sortConfig.field]?.percentage_of_aum ?? -1;
        const bPct = b.schemes[sortConfig.field]?.percentage_of_aum ?? -1;
        return mul * (aPct - bPct);
      });
  }, [pivotedRows, sortConfig, hiddenSchemeIds]);

  const sortIcon = (field: string) => (
    <span className={pageStyles['sort-icon']}>
      <Icon
        name={sortConfig.field !== field ? 'sortable' : sortConfig.dir === 'asc' ? 'sortAsc' : 'sortDesc'}
        size={13}
      />
    </span>
  );

  // Response-level figures — the table's own totals stay in its footer row.
  const stats: StatItem[] | undefined = showLoader
    ? loadingStats(['Total AUM', 'Holding date', 'Holdings'])
    : summary ? [
    {
      label: 'Total AUM',
      value: formatCurrencyInCrores(visibleSummary.totalAum, summary.base_currency, false, 1),
    },
    {
      label: 'Holding date',
      value: summary.holding_date ? formatDate(summary.holding_date) : '—',
    },
    {
      label: 'Holdings',
      value: formatNumber(sortedRows.length, 0),
      hint: `${visibleSchemes.length} scheme${visibleSchemes.length === 1 ? '' : 's'} shown`,
    },
  ] : undefined;

  return (
    <PageFrame
      header={
        <PageHeader
          title="Scheme-wise Holdings"
          subtitle="Every holding across the selected schemes, side by side."
        />
      }
    >
      <Surface padded>
        <div className={pageStyles['filter-row']}>
          <LastWorkingDateFilter
            value={selectedDate}
            onChange={(date) => {
              // Enforce max date — snap back to accessCutoffDate if user types a too-recent date
              setSelectedDate(clampDate(date));
            }}
            label="Holding Date"
            inputId="swh-date"
            max={accessCutoffDate ?? undefined}
          />

          {assetClasses.length > 0 && (
            <div className={s['asset-class-dropdown-wrapper']}>
              <Button
                ref={assetClassBtnRef}
                aria-expanded={showAssetClassDropdown}
                aria-haspopup="listbox"
                onClick={() => setShowAssetClassDropdown(p => !p)}
              >
                Asset Class
                <span className={s['visibility-count']}>
                  {assetClasses.filter(ac => !hiddenAssetClassIds.has(ac.id)).length} / {assetClasses.length}
                </span>
              </Button>
              {showAssetClassDropdown && assetClassPos && createPortal(
                <div
                  ref={assetClassPanelRef}
                  className={s['asset-class-dropdown']}
                  style={{ top: assetClassPos.top, left: assetClassPos.left }}
                  role="listbox"
                  aria-label="Asset classes"
                >
                  {assetClasses.map(ac => {
                    const hidden = hiddenAssetClassIds.has(ac.id);
                    return (
                      <label key={ac.id} className={s['asset-class-option']}>
                        <input
                          type="checkbox"
                          checked={!hidden}
                          onChange={() => toggleAssetClass(ac.id)}
                        />
                        {ac.name}
                      </label>
                    );
                  })}
                </div>,
                document.body,
              )}
            </div>
          )}

          <SchemeFilterPanel
            onFilterChange={(schemes, hidden) => {
              setRegionSchemes(schemes);
              setHiddenSchemeIds(hidden);
            }}
            onError={setSchemesError}
            onRegionChange={() => setSortConfig({ field: 'symbol', dir: 'asc' })}
          />

          <label className={s['exclude-inherited-label']}>
            <input
              type="checkbox"
              checked={excludeInherited}
              onChange={(e) => setExcludeInherited(e.target.checked)}
            />
            <span style={{ marginLeft: 4 }}>Exclude Inherited</span>
          </label>
        </div>
      </Surface>

      {stats && stats.length > 0 && (
        <StatGrid items={stats} />
      )}

      {error && <Note tone="error">{error}</Note>}
      {isAccessRestricted && (
        <Note tone="warning">
          Data for this date requires complete access. Contact your administrator.
        </Note>
      )}

      {showLoader && (
        <Surface>
          <Placeholder kind="loading" message="Loading holdings data…" />
        </Surface>
      )}

      {/* Comparison table */}
      {!showLoader && !error && sortedRows.length > 0 && visibleSchemes.length > 0 && (
        <Surface>
          <div className={pageStyles['table-scroll']}>
            <table className={`${pageStyles.table} ${s['table-fix']}`}>
              <thead>
                {/* Row 1: fixed columns + scheme name spanning headers */}
                <tr ref={headerRow1Ref}>
                  <th rowSpan={2} style={{ width: 40 }}>
                    #
                  </th>
                  <th
                    rowSpan={2}
                    className={[
                      pageStyles['th-sortable'],
                      sortConfig.field === 'symbol' ? pageStyles['th-active'] : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleSort('symbol')}
                  >
                    Symbol / Name {sortIcon('symbol')}
                  </th>
                  <th rowSpan={2}>Asset Class</th>
                  {visibleSchemes.map(sc => {
                    const lastDash = sc.label.lastIndexOf(' - ');
                    const line1 = lastDash !== -1 ? sc.label.slice(0, lastDash) : sc.label;
                    const line2 = lastDash !== -1 ? sc.label.slice(lastDash + 3) : null;
                    return (
                      <th key={sc.value} colSpan={isAifScheme(sc) ? 2 : 3} className={s['scheme-header-cell']}>
                        <Tooltip title={sc.fullName || ''} placement="top" arrow>
                          <span>
                            {line1}
                            {line2 && <><br /><span style={{ fontWeight: 400 }}>{line2}</span></>}
                          </span>
                        </Tooltip>
                      </th>
                    );
                  })}
                </tr>
                {/* Row 2: per-scheme sub-headers — sticky just below row 1 */}
                <tr>
                  {visibleSchemes.map(sc => (
                    <React.Fragment key={sc.value}>
                      <th
                        className={`${pageStyles['text-right']} ${s['scheme-col']}`}
                        style={{ top: row1Height }}
                      >
                        Market<br />Value
                      </th>
                      <th
                        className={[
                          pageStyles['th-sortable'],
                          sortConfig.field === sc.value ? pageStyles['th-active'] : '',
                          s['scheme-col'],
                          pageStyles['text-right'],
                        ].filter(Boolean).join(' ')}
                        style={{ top: row1Height }}
                        onClick={() => handleSort(sc.value)}
                      >
                        % of AUM {sortIcon(sc.value)}
                      </th>
                      {!isAifScheme(sc) && (
                        <th
                          className={`${pageStyles['text-right']} ${s['scheme-col']}`}
                          style={{ top: row1Height }}
                        >
                          Clients
                        </th>
                      )}
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, idx) => (
                  <tr key={row.company_id}>
                    <td
                      style={{ fontWeight: 600, color: 'var(--text-dim)', fontSize: 12 }}
                    >
                      {idx + 1}
                    </td>
                    <td style={{ fontWeight: 600 }}>{row.asset_class === 'Mutual Fund' ? (row.name || row.symbol) : row.symbol}</td>
                    <td>
                      <span className={`${s.badge} ${s[ASSET_CLASS_MOD[row.asset_class] ?? 'badge-other']}`}>
                        {row.asset_class}
                      </span>
                    </td>
                    {visibleSchemes.map(sc => {
                      const h = row.schemes[sc.value];
                      return (
                        <React.Fragment key={sc.value}>
                          <td className={`${pageStyles['text-right']} ${s['scheme-col']}`}>
                            {h ? formatCurrencyInCrores(h.market_value, summary?.base_currency, false, 1) : <span className={s['no-data']}>—</span>}
                          </td>
                          <td className={`${pageStyles['text-right']} ${s['scheme-col']}`}>
                            {h ? formatPercentage(h.percentage_of_aum, 1) : <span className={s['no-data']}>—</span>}
                          </td>
                          {!isAifScheme(sc) && (
                            <td className={`${pageStyles['text-right']} ${s['scheme-col']}`}>
                              {h ? h.unique_clients : <span className={s['no-data']}>—</span>}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={s['tfoot-row']}>
                  <th colSpan={3} className={s['tfoot-cell']}>
                    Total
                  </th>
                  {visibleSchemes.map(sc => {
                    const t = schemeTotals[sc.value] ?? { market_value: 0, percentage_of_aum: 0 };
                    return (
                      <React.Fragment key={sc.value}>
                        <th className={`${s['tfoot-cell']} ${s['scheme-col']} ${pageStyles['text-right']}`}>
                          {formatCurrencyInCrores(t.market_value, summary?.base_currency, false, 1)}
                        </th>
                        <th className={`${s['tfoot-cell']} ${s['scheme-col']} ${pageStyles['text-right']}`}>
                          {formatPercentage(t.percentage_of_aum, 1)}
                        </th>
                        {!isAifScheme(sc) && <th className={`${s['tfoot-cell']} ${s['scheme-col']} ${pageStyles['text-right']}`} />}
                      </React.Fragment>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </Surface>
      )}

      {!showLoader && !error && selectedDate && sortedRows.length === 0 && (
        <Surface>
          <Placeholder message="No holdings found for the selected date." />
        </Surface>
      )}

      {!showLoader && !error && selectedDate && sortedRows.length > 0 && visibleSchemes.length === 0 && (
        <Surface>
          <Placeholder
            message="All schemes are hidden"
            hint="Use “Show Filters” to restore visibility."
          />
        </Surface>
      )}
    </PageFrame>
  );
};

export default SchemeWiseHoldings;